import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ship, Plane, ArrowLeft, Calendar, Package, MapPin, AlertTriangle, Info, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import PortSelector from '../components/PortSelector';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const vesselTypes = [
  { value: 'container', label: 'Container Ship' },
  { value: 'bulk_carrier', label: 'Bulk Carrier' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'roro', label: 'RoRo (Roll-on/Roll-off)' },
  { value: 'tug', label: 'Tug' },
  { value: 'barge', label: 'Barge' },
  { value: 'general_cargo', label: 'General Cargo' },
  { value: 'reefer', label: 'Reefer (Refrigerated)' },
  { value: 'chemical_tanker', label: 'Chemical Tanker' },
  { value: 'oil_tanker', label: 'Oil Tanker' },
  { value: 'lpg_carrier', label: 'LPG Carrier' },
  { value: 'lng_carrier', label: 'LNG Carrier' },
  { value: 'car_carrier', label: 'Car Carrier' },
  { value: 'heavy_lift', label: 'Heavy Lift' },
  { value: 'offshore_supply', label: 'Offshore Supply Vessel' },
  { value: 'multipurpose', label: 'Multipurpose' },
  { value: 'passenger_ferry', label: 'Passenger/Cargo Ferry' },
  { value: 'other', label: 'Other' }
];

const charterTerms = [
  { value: 'voyage', label: 'Voyage Charter - Single voyage' },
  { value: 'time', label: 'Time Charter - Period rental' },
  { value: 'bareboat', label: 'Bareboat Charter - Vessel only, no crew' },
  { value: 'spot', label: 'Spot Charter - Immediate availability' },
  { value: 'coa', label: 'COA - Contract of Affreightment' }
];

const cargoUnits = [
  { value: 'MT', label: 'Metric Tons (MT)' },
  { value: 'CBM', label: 'Cubic Meters (CBM)' },
  { value: 'TEU', label: 'TEU (20ft containers)' },
  { value: 'FEU', label: 'FEU (40ft containers)' },
  { value: 'units', label: 'Units/Pieces' },
  { value: 'kg', label: 'Kilograms (kg)' }
];

const CreateCharterRFQ = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [charterType, setCharterType] = useState('vessel');
  
  const [formData, setFormData] = useState({
    title: '',
    charter_type: 'vessel',
    vessel_type_required: '',
    vessel_size_min_dwt: '',
    vessel_size_max_dwt: '',
    origin_port: '',
    destination_port: '',
    laycan_start: '',
    laycan_end: '',
    cargo_description: '',
    cargo_quantity: '',
    cargo_unit: 'MT',
    is_hazardous: false,
    hazmat_class: '',
    requires_special_equipment: [],
    charter_terms: '',
    duration_days: '',
    special_requirements: '',
    preferred_flag_states: [],
    max_vessel_age: '',
    required_certifications: [],
    budget_indication: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    is_public: true
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.origin_port || !formData.destination_port || !formData.cargo_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        charter_type: charterType,
        cargo_quantity: formData.cargo_quantity ? parseFloat(formData.cargo_quantity) : null,
        vessel_size_min_dwt: formData.vessel_size_min_dwt ? parseFloat(formData.vessel_size_min_dwt) : null,
        vessel_size_max_dwt: formData.vessel_size_max_dwt ? parseFloat(formData.vessel_size_max_dwt) : null,
        duration_days: formData.duration_days ? parseInt(formData.duration_days) : null,
        max_vessel_age: formData.max_vessel_age ? parseInt(formData.max_vessel_age) : null
      };

      const res = await fetch(`${API}/api/charter-rfqs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Charter request created successfully!');
        navigate(`/charters/${data.rfq_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create charter request');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Page Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/charters')}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Charters
            </Button>
            <h1 className="text-3xl font-bold text-slate-900">Request a Charter</h1>
            <p className="text-slate-600 mt-1">Create a request for vessel or cargo flight charter</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Charter Type Selection */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Charter Type</CardTitle>
              <CardDescription>Select the type of charter you need</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setCharterType('vessel')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    charterType === 'vessel' 
                      ? 'border-cyan-500 bg-cyan-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="charter-type-vessel"
                >
                  <Ship className={`h-12 w-12 mx-auto mb-3 ${charterType === 'vessel' ? 'text-cyan-600' : 'text-slate-400'}`} />
                  <h3 className="font-semibold text-lg">Vessel Charter</h3>
                  <p className="text-sm text-slate-500 mt-1">Ships, tugs, barges, tankers, etc.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setCharterType('cargo_flight')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    charterType === 'cargo_flight' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="charter-type-flight"
                >
                  <Plane className={`h-12 w-12 mx-auto mb-3 ${charterType === 'cargo_flight' ? 'text-purple-600' : 'text-slate-400'}`} />
                  <h3 className="font-semibold text-lg">Cargo Flight</h3>
                  <p className="text-sm text-slate-500 mt-1">Air cargo charter services</p>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Request Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Bulk Carrier needed for grain shipment"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  data-testid="charter-title"
                />
              </div>

              {charterType === 'vessel' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vessel Type Required</Label>
                    <Select
                      value={formData.vessel_type_required}
                      onValueChange={(v) => handleChange('vessel_type_required', v)}
                    >
                      <SelectTrigger data-testid="vessel-type-select">
                        <SelectValue placeholder="Select vessel type" />
                      </SelectTrigger>
                      <SelectContent>
                        {vesselTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Charter Terms</Label>
                    <Select
                      value={formData.charter_terms}
                      onValueChange={(v) => handleChange('charter_terms', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select charter terms" />
                      </SelectTrigger>
                      <SelectContent>
                        {charterTerms.map(term => (
                          <SelectItem key={term.value} value={term.value}>{term.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {charterType === 'vessel' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Vessel Size (DWT)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 5000"
                      value={formData.vessel_size_min_dwt}
                      onChange={(e) => handleChange('vessel_size_min_dwt', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Max Vessel Size (DWT)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 50000"
                      value={formData.vessel_size_max_dwt}
                      onChange={(e) => handleChange('vessel_size_max_dwt', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {formData.charter_terms === 'time' && (
                <div>
                  <Label>Duration (Days)</Label>
                  <Input
                    type="number"
                    placeholder="Number of days"
                    value={formData.duration_days}
                    onChange={(e) => handleChange('duration_days', e.target.value)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Route */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Route
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Origin Port *</Label>
                  <PortSelector
                    value={formData.origin_port}
                    onChange={(v) => handleChange('origin_port', v)}
                    placeholder="Select loading port"
                  />
                </div>
                <div>
                  <Label>Destination Port *</Label>
                  <PortSelector
                    value={formData.destination_port}
                    onChange={(v) => handleChange('destination_port', v)}
                    placeholder="Select discharge port"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Laycan Start (Earliest Load Date)
                  </Label>
                  <Input
                    type="date"
                    value={formData.laycan_start}
                    onChange={(e) => handleChange('laycan_start', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Laycan End (Latest Load Date)
                  </Label>
                  <Input
                    type="date"
                    value={formData.laycan_end}
                    onChange={(e) => handleChange('laycan_end', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cargo Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Cargo Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Cargo Description *</Label>
                <Textarea
                  placeholder="Describe the cargo (type, nature, packaging, etc.)"
                  value={formData.cargo_description}
                  onChange={(e) => handleChange('cargo_description', e.target.value)}
                  rows={3}
                  required
                  data-testid="cargo-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cargo Quantity</Label>
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={formData.cargo_quantity}
                    onChange={(e) => handleChange('cargo_quantity', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select
                    value={formData.cargo_unit}
                    onValueChange={(v) => handleChange('cargo_unit', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cargoUnits.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Checkbox
                  id="hazardous"
                  checked={formData.is_hazardous}
                  onCheckedChange={(checked) => handleChange('is_hazardous', checked)}
                />
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <Label htmlFor="hazardous" className="text-amber-800">
                    Hazardous/Dangerous Cargo
                  </Label>
                </div>
              </div>

              {formData.is_hazardous && (
                <div>
                  <Label>HAZMAT Class/UN Number</Label>
                  <Input
                    placeholder="e.g., Class 3, UN1203"
                    value={formData.hazmat_class}
                    onChange={(e) => handleChange('hazmat_class', e.target.value)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Additional Requirements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Special Requirements</Label>
                <Textarea
                  placeholder="Any specific requirements (equipment, certifications, crew, etc.)"
                  value={formData.special_requirements}
                  onChange={(e) => handleChange('special_requirements', e.target.value)}
                  rows={3}
                />
              </div>
              {charterType === 'vessel' && (
                <div>
                  <Label>Max Vessel Age (Years)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 20"
                    value={formData.max_vessel_age}
                    onChange={(e) => handleChange('max_vessel_age', e.target.value)}
                  />
                </div>
              )}
              <div>
                <Label>Budget Indication</Label>
                <Input
                  placeholder="e.g., $50,000 - $75,000 or Open"
                  value={formData.budget_indication}
                  onChange={(e) => handleChange('budget_indication', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>How respondents should contact you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    placeholder="Your name"
                    value={formData.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="contact@company.com"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    placeholder="+1 234 567 8900"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visibility */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="public"
                  checked={formData.is_public}
                  onCheckedChange={(checked) => handleChange('is_public', checked)}
                />
                <Label htmlFor="public">Make this request visible to all members</Label>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/charters')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-cyan-500 hover:bg-cyan-600"
              disabled={loading}
              data-testid="submit-charter-btn"
            >
              {loading ? 'Creating...' : 'Create Charter Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default CreateCharterRFQ;
